import type { CardInfo } from '@lub0v/santa-utils';
import type { DrawAssignment } from './draw';

/** Complete a partial draw without moving any selected recipient. Zero means unassigned. */
export function completeManualDraw(
  cards: CardInfo[],
  partial: DrawAssignment[],
  circle: boolean,
  shuffle = false,
): DrawAssignment[] | null {
  const ids = cards.map(card => card.id);
  const byId = new Map(cards.map(card => [card.id, card]));
  if (partial.length !== cards.length || new Set(partial.map(pair => pair.id)).size !== cards.length) return null;
  const colors = new Map(cards.map(card => [card.id, (card.drawColors || []).filter(color => color && !['#FFF', '#FFFFFF'].includes(color.toUpperCase()))]));
  const allowed = new Map(cards.map(card => [card.id, ids.filter(id => id !== card.id && !(card.exclusions || []).includes(id) && !colors.get(card.id)!.some(color => colors.get(id)!.includes(color)))]));
  const selected = new Map<number, number>();
  const used = new Set<number>();
  for (const pair of partial) {
    if (!byId.has(pair.id)) return null;
    if (!pair.wardId) continue;
    if (!allowed.get(pair.id)!.includes(pair.wardId) || used.has(pair.wardId)) return null;
    selected.set(pair.id, pair.wardId);
    used.add(pair.wardId);
  }
  const closesShortCycle = (giver: number, recipient: number) => {
    let current = recipient;
    let length = 1;
    const seen = new Set<number>();
    while (selected.has(current) && !seen.has(current)) {
      if (current === giver) break;
      seen.add(current);
      current = selected.get(current)!;
      length++;
    }
    return current === giver && length < cards.length;
  };
  if (circle && [...selected].some(([giver, recipient]) => closesShortCycle(giver, recipient))) return null;

  const ordered = (values: number[]) => {
    if (!shuffle) return values;
    const next = [...values];
    for (let i = next.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [next[i], next[j]] = [next[j], next[i]];
    }
    return next;
  };
  // Normal draws are bipartite matchings: augmenting paths avoid exponential searches.
  if (!circle) {
    const owners = new Map([...selected].map(([giver, recipient]) => [recipient, giver]));
    const fixed = new Set(selected.keys());
    const match = (giver: number, visited: Set<number>): boolean => {
      for (const recipient of ordered(allowed.get(giver)!).sort((a, b) => Number(owners.has(a)) - Number(owners.has(b)))) {
        if (visited.has(recipient)) continue;
        visited.add(recipient);
        const owner = owners.get(recipient);
        if (owner === undefined || (!fixed.has(owner) && match(owner, visited))) {
          owners.set(recipient, giver);
          selected.set(giver, recipient);
          return true;
        }
      }
      return false;
    };
    for (const giver of ordered(ids.filter(id => !fixed.has(id)))) {
      if (!match(giver, new Set())) return null;
    }
  } else {
    // Reject premature cycles and use the most constrained giver first. Bound work
    // for difficult exclusion sets; callers keep the draft intact if no completion is found.
    let attempts = 0;
    const search = (): boolean => {
      if (selected.size === ids.length) return true;
      if (++attempts > 50_000) return false;
      let giver = 0;
      let choices: number[] | null = null;
      for (const id of ids) {
        if (selected.has(id)) continue;
        const possible = allowed.get(id)!.filter(recipient => !used.has(recipient) && !closesShortCycle(id, recipient));
        if (!possible.length) return false;
        if (!choices || possible.length < choices.length) { giver = id; choices = possible; }
      }
      for (const recipient of ordered(choices!)) {
        selected.set(giver, recipient);
        used.add(recipient);
        if (search()) return true;
        selected.delete(giver);
        used.delete(recipient);
      }
      return false;
    };
    if (!search()) return null;
  }
  return partial.map(pair => ({ id: pair.id, wardId: selected.get(pair.id)! }));
}
